import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  Switch,
  Typography,
  Grid,
  Divider,
  Box,
  InputLabel,
  RadioGroup,
  Radio,
  FormLabel,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Select from 'react-select';
import apiUrl from "../api";
import { format } from "date-fns";
import PopUpWindow from "../popUpWindow/PopUpWindow";

const ContractForm = ({ open, handleClose, contract = null, onSave, refreshContracts, readOnly = false, noPopUp = false }) => {
  const isEdit = Boolean(contract);
  const [formData, setFormData] = useState({
    title: "",
    properties: [],
    start_date: new Date(),
    end_date: new Date(),
    has_end_date: true,
    charge: 0,
    calculate_monthly: true,
    calculate_daily: false,
    charge_daily: false,
    charge_monthly: true,
    charge_annually: false,
    notes: "",
    active: true
  });
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const user = JSON.parse(localStorage.getItem("user"));

  // Fetch properties for dropdown using the dedicated getPropertiesNames endpoint
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const token = user?.token
        const response = await fetch(`${apiUrl}/property/getPropertiesNames`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setProperties(data.propertiesNames.map(property => ({
            value: property._id,
            label: property.name
          })));
        } else {
          console.error("Failed to fetch properties");
        }
      } catch (error) {
        console.error("Error fetching properties:", error);
      }
    };

    fetchProperties();
  }, [user?.token]);

  // Set form data if editing an existing contract
  useEffect(() => {
    if (contract) {
      // Handle backwards compatibility - contract might have either property or properties
      let contractProperties = [];
      
      if (contract.properties && contract.properties.length > 0) {
        // New format: array of properties
        contractProperties = contract.properties.map(p => p._id || p);
      } else if (contract.property) {
        // Old format: single property
        contractProperties = [contract.property._id || contract.property];
      }
      
      setFormData({
        title: contract.title,
        properties: contractProperties,
        start_date: new Date(contract.start_date),
        end_date: contract.end_date ? new Date(contract.end_date) : null,
        has_end_date: !!contract.end_date,
        charge: contract.charge,
        calculate_monthly: contract.calculate_monthly,
        calculate_daily: contract.calculate_daily,
        charge_daily: contract.charge_daily || false,
        charge_monthly: contract.charge_monthly || true,
        charge_annually: contract.charge_annually || false,
        notes: contract.notes || "",
        active: contract.active
      });
    }
  }, [contract]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Special handling for has_end_date switch
    if (name === 'has_end_date') {
      setFormData({
        ...formData,
        has_end_date: checked,
        end_date: checked ? formData.end_date || new Date() : null
      });
      return;
    }
    
    // Special handling for calculate switches
    if (name === 'calculate_monthly' || name === 'calculate_daily') {
      // If trying to turn off the last active switch, prevent it
      if (!checked && 
          ((name === 'calculate_monthly' && !formData.calculate_daily) || 
           (name === 'calculate_daily' && !formData.calculate_monthly))) {
        return;
      }
      
      // If turning on one switch, turn off the other
      if (checked) {
        setFormData({
          ...formData,
          calculate_monthly: name === 'calculate_monthly',
          calculate_daily: name === 'calculate_daily'
        });
        return;
      }
    }
    
    // Special handling for charge frequency radios
    if (name === 'charge_frequency') {
      setFormData({
        ...formData,
        charge_daily: value === 'daily',
        charge_monthly: value === 'monthly',
        charge_annually: value === 'annually'
      });
      return;
    }

    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSelectChange = (selectedOptions, { name }) => {
    if (name === 'properties') {
      setFormData({
        ...formData,
        [name]: selectedOptions ? selectedOptions.map(option => option.value) : [],
      });
    } else {
      setFormData({
        ...formData,
        [name]: selectedOptions?.value,
      });
    }
  };

  const handleDateChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      const token = user?.token;
      const method = isEdit ? "PUT" : "POST";
      const url = isEdit
        ? `${apiUrl}/contract/${contract._id}`
        : `${apiUrl}/contract`;

      // Format dates for API and handle end_date
      const apiFormData = {
        ...formData,
        start_date: format(formData.start_date, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
        end_date: formData.has_end_date && formData.end_date
          ? format(formData.end_date, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx") 
          : null
      };

      // Remove has_end_date as it's not needed in the API
      delete apiFormData.has_end_date;

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(apiFormData),
      });

      const data = await response.json();
      
      if (response.ok) {
        if (onSave) onSave(data.contract);
        if (refreshContracts) refreshContracts();
        handleClose();
      } else {
        setError(data.message || "Failed to save contract");
      }
    } catch (err) {
      setError("An error occurred while saving the contract");
      console.error("Contract save error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Determine the current charge frequency value for radio buttons
  const getChargeFrequencyValue = () => {
    if (formData.charge_daily) return 'daily';
    if (formData.charge_monthly) return 'monthly';
    if (formData.charge_annually) return 'annually';
    return 'monthly'; // Default to monthly
  };

  // Get the currently selected property options
  const selectedPropertyOptions = properties.filter(option => 
    formData.properties.includes(option.value)
  ) || [];
  
  // Custom styles for react-select
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '56px',
      borderColor: '#c4c4c4',
      boxShadow: state.isFocused ? '0 0 0 1px #1976d2' : null,
      '&:hover': {
        borderColor: state.isFocused ? '#1976d2' : '#c4c4c4',
      },
    }),
    menu: base => ({
      ...base,
      zIndex: 10001,
      position: 'absolute',
      width: '100%',
    }),
    menuPortal: base => ({
      ...base,
      zIndex: 10001,
    }),
    container: base => ({
      ...base,
      width: '100%',
      position: 'relative',
    }),
  };

  // Custom styles for date pickers to ensure they appear above other elements
  const datePickerSx = {
    '& .MuiPopover-root, & .MuiPaper-root, & .MuiDialog-root, & .MuiModal-root, & .MuiPopper-root': {
      zIndex: 1000001    }
  };

  if (!open && !noPopUp) return null;

  const formContent = (
    <Box sx={{ p: 2 }}>
      {error && (
        <Box my={2}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            name="title"
            label="Contract Title"
            fullWidth
            value={formData.title}
            onChange={handleChange}
            required
            disabled={readOnly}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box sx={{ mb: 1 }}>
            <InputLabel htmlFor="properties-select">Properties *</InputLabel>
            <Select
              id="properties-select"
              name="properties"
              value={selectedPropertyOptions}
              onChange={handleSelectChange}
              options={properties}
              isDisabled={readOnly}
              styles={selectStyles}
              placeholder="Select Properties"
              menuPlacement="auto"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              classNamePrefix="select"
              isClearable={!readOnly}
              isMulti={true}
              closeMenuOnSelect={false}
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={formData.start_date}
              onChange={(newValue) => handleDateChange("start_date", newValue)}
              disabled={readOnly}
              slotProps={{
                popper: {
                  style: { zIndex: 1000001 },
                  sx: datePickerSx
                },
                textField: {
                  fullWidth: true
                }
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box>
            <FormControlLabel
              control={
                <Switch
                  name="has_end_date"
                  checked={formData.has_end_date}
                  onChange={handleChange}
                  disabled={readOnly}
                />
              }
              label="Has End Date"
            />
          </Box>
          {formData.has_end_date && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={formData.end_date}
                onChange={(newValue) => handleDateChange("end_date", newValue)}
                disabled={readOnly || !formData.has_end_date}
                slotProps={{
                  popper: {
                    style: { zIndex: 1000001 },
                    sx: datePickerSx
                  },
                  textField: {
                    fullWidth: true
                  }
                }}
              />
            </LocalizationProvider>
          )}
        </Grid>

        <Grid item xs={12}>
          <Divider />
          <Typography variant="subtitle1" mt={2} mb={1}>
            Pricing Details
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            name="charge"
            label="Charge Amount"
            type="number"
            fullWidth
            value={formData.charge}
            onChange={handleChange}
            disabled={readOnly}
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1 }}>€</Typography>,
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControl component="fieldset" disabled={readOnly}>
            <FormLabel component="legend">Charge Frequency</FormLabel>
            <RadioGroup
              row
              name="charge_frequency"
              value={getChargeFrequencyValue()}
              onChange={handleChange}
            >
              <FormControlLabel value="daily" control={<Radio />} label="Daily" />
              <FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
              <FormControlLabel value="annually" control={<Radio />} label="Annually" />
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                name="calculate_monthly"
                checked={formData.calculate_monthly}
                onChange={handleChange}
                disabled={readOnly}
              />
            }
            label="Fixed Charge"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                name="calculate_daily"
                checked={formData.calculate_daily}
                onChange={handleChange}
                disabled={readOnly}
              />
            }
            label="Charge Amount Calculated per Day"
          />
        </Grid>

        <Grid item xs={12}>
          <Divider />
          <Typography variant="subtitle1" mt={2} mb={1}>
            Additional Information
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            name="notes"
            label="Notes"
            multiline
            rows={4}
            fullWidth
            value={formData.notes}
            onChange={handleChange}
            disabled={readOnly}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                name="active"
                checked={formData.active}
                onChange={handleChange}
                disabled={readOnly}
              />
            }
            label="Contract Active"
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button onClick={handleClose} sx={{ mr: 1 }}>
          {readOnly ? "Close" : "Cancel"}
        </Button>
        {!readOnly && (
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary" 
            disabled={loading}
          >
            {loading ? "Saving..." : isEdit ? "Update" : "Create"}
          </Button>
        )}
      </Box>
    </Box>
  );

  if (noPopUp) {
    return formContent;
  }

  return (
    <PopUpWindow 
      title={readOnly ? "View Contract Details" : isEdit ? "Edit Contract" : "Create New Contract"}
      onClose={handleClose}
      width="80%"
      color="#ffffff"
    >
      {formContent}
    </PopUpWindow>
  );
};

export default ContractForm;
